import React, { useEffect, useState } from 'react';
import { Img } from 'react-image';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Seo from 'src/components/Seo';
import ApplicationDrawer from 'src/components/my-applications/ApplicationDrawer';
import MyApplicationsBreadcrumbs from 'src/components/my-applications/MyApplicationsBreadcrumbs';
import NoApplicationsContainer from 'src/components/my-applications/NoApplicationsContainer';
import Container from 'src/components/shared/Container';
import { useGetAllApplications } from 'src/hooks/application.hooks';
import { ApplicationStatus, UIExtendedApplication, UIApplicationType, ApplicationRoundType, PositionTitle, UserCareerPathType } from 'types';
import { Loader } from 'ui';
import seoConfig from '../seo.json';
import ApplicationCardV2 from 'src/components/my-applications/application-card-v2/ApplicationCardV2';
import ViewAssignment from 'src/components/my-applications/assignment/ViewAssignment';
import ViewOffer from 'src/components/my-applications/offer/ViewOffer';
import NotInterestedModal from 'src/components/my-applications/application-card-v2/NotInterestedModal';
import CancelInterviewModal from 'src/components/my-applications/application-card-v2/CancelInterviewModal';
import LinkedInResumeModal from 'src/components/my-applications/LinkedInResumeModal';
import { useMutation, useQueryClient } from 'react-query';
import { createApplicationV2, optOutOfApplicationProcess } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import ViewUpdatedTerms from 'src/components/my-applications/offer/ViewUpdatedTerms';
import { get } from 'lodash';
import { Pagination, ToggleSwitch, Button } from 'flowbite-react';
import { useMediaQuery } from '@mui/material';
import { CompanyLogo, useDriveFont } from 'src/components/placementDrives/shared';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';
import ViewOnlineTest from 'src/components/my-applications/online-test/ViewOnlineTest';
import { formatCareerPathType, revertPositionTitle } from 'shared-utils';
import { sendTrackingEvent } from 'src/utils/gtm';
import UpcomingDrives from 'src/components/placementDrives/UpcomingDrives';

export interface ApplicationDrawerDetail {
	id: string;
	workingMode: string;
	workingExp: string;
	minSalary: number;
	maxSalary: number;
	location: string;
	jobType: string;
	description: string;
	companyLogo: string;
	companyUrl: string;
	companyName: string;
	companyPosition: string;
	isLoadingApplying: boolean;
	isPositionClosed: boolean;
	hasExpiryDate: boolean;
	postedOn: string;
	expiresOn: string | null;
	updatedAt: string;
	minInternshipSalary?: number;
	maxInternshipSalary?: number;
	internshipDuration?: number;
	hasBond?: boolean;
	bondDetails?: string;
}

// Loads the shared drive-flow font and styles while an application detail view is open.
const DetailStyleLoader = () => {
	useDriveFont();
	return null;
};

const MyApplications = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const seoInfo = seoConfig['my-applications'];
	const isMobile = useMediaQuery('(max-width:960px)');
	const [searchParams, setSearchParams] = useSearchParams();
	const page = searchParams.get('page') || '1';
	const limit = searchParams.get('limit') || '10';
	const viewAssignmentSearchParam = searchParams.get('viewAssignment');
	const viewOnlineTestSearchParam = searchParams.get('viewOnlineTest');
	const viewOfferSearchParam = searchParams.get('viewOffer');
	const viewUpdatedTermsSearchParam = searchParams.get('viewUpdatedTerms');
	const [showRecommendedOnly, setShowRecommendedOnly] = useState(false);
	const { data: allApplications, isLoading: isAllApplicationsLoading } = useGetAllApplications({ page, limit, showRecommendedOnly: showRecommendedOnly ? 'true' : 'false' });
	const applicationTabs: {
		[key: string]: {
			text: string;
			total: number;
			applications: UIExtendedApplication[];
		};
	} = {
		matchedApplications: {
			text: 'Matched',
			total: allApplications?.applications?.totalMatchedApplications || 0,
			applications: allApplications?.applications?.matchedApplications || [],
		},
		appliedApplications: {
			text: 'Applied',
			total: allApplications?.applications?.totalAppliedApplications || 0,
			applications: allApplications?.applications?.appliedApplications || [],
		},
		ongoingApplications: {
			text: 'Ongoing',
			total: allApplications?.applications?.totalOngoingApplications || 0,
			applications: allApplications?.applications?.ongoingApplications || [],
		},
		closedApplications: {
			text: 'Closed',
			total: allApplications?.applications?.totalClosedApplications || 0,
			applications: allApplications?.applications?.closedApplications || [],
		},
	};
	const [selectedTab, setSelectedTab] = useState<UIApplicationType>(
		(searchParams.get('applicationType') as UIApplicationType) || 'matchedApplications',
	);

	const [isApplicationDrawerOpen, setIsApplicationDrawerOpen] = useState<boolean>(false);
	const [applicationDrawerDetail, setApplicationDrawerDetail] = useState<ApplicationDrawerDetail>({
		id: '',
		workingMode: '',
		workingExp: '',
		minSalary: 0,
		maxSalary: 0,
		location: '',
		jobType: '',
		description: '',
		companyLogo: '',
		companyUrl: '',
		companyName: '',
		companyPosition: '',
		isLoadingApplying: false,
		isPositionClosed: false,
		hasExpiryDate: false,
		postedOn: '',
		expiresOn: null,
		updatedAt: '',
		minInternshipSalary: 0,
		maxInternshipSalary: 0,
		internshipDuration: 0,
		hasBond: false,
		bondDetails: '',
	});
	const [showCta, setShowCta] = useState<boolean>(false);
	const [myApplicationStatus, setMyApplicationStatus] = useState<ApplicationStatus>(ApplicationStatus.OPENED_BY_ADMIN);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [isApplyModalOpen, setIsApplyModalOpen] = useState<boolean>(false);

	const handleCardClick = (
		arg: ApplicationDrawerDetail,
		showCtaFlag: boolean,
		applicationStatus: ApplicationStatus,
		application: UIExtendedApplication,
	) => {
		setApplicationDrawerDetail(arg);
		setShowCta(showCtaFlag);
		setMyApplicationStatus(applicationStatus);
		setIsApplicationDrawerOpen(true);
		setCurrentlySelectedApplication(application);
	};
	const [isNotInterestedModalOpen, setIsNotInterestedModalOpen] = useState(false);
	// this state is added for modals, drawers to access current application on which user is doing some action
	const [currentlySelectedApplication, setCurrentlySelectedApplication] = useState<UIExtendedApplication>(
		{} as UIExtendedApplication,
	);

	const [isCancelInterviewModalOpen, setIsCancelInterviewModalOpen] = useState(false);
	const [isLinkedInModalOpen, setIsLinkedInModalOpen] = useState(false);
	const [isLinkedInResumeModalOpen, setIsLinkedInResumeModalOpen] = useState(false);

	const toast = useToast();
	const queryClient = useQueryClient();
	const [optOutOfApplicationProcessLoading, setOptOutOfApplicationProcessLoading] = useState(false);
	const optOutOfApplicationProcessMutation = useMutation(['optOutOfApplicationProcess'], optOutOfApplicationProcess, {
		onSuccess() {
			toast({
				title: 'You have opted out of the application process.',
				type: 'success',
			});
			setIsNotInterestedModalOpen(false);
			queryClient.refetchQueries(['getAllApplications']);
			setOptOutOfApplicationProcessLoading(false);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			setOptOutOfApplicationProcessLoading(false);
			toast({
				title: `Error while opting out of the application process: ${errorMessage}`,
				type: 'error',
			});
		},
	});

	const [createApplicationLoading, setCreateApplicationLoading] = React.useState(false);
	const createApplicationMutation = useMutation(['createApplication'], createApplicationV2, {
		onSuccess() {
			toast({
				title: 'Application created successfully',
				type: 'success',
			});
			queryClient.refetchQueries(['getAllApplications']);
			setCreateApplicationLoading(false);
			setIsLinkedInResumeModalOpen(false);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			setCreateApplicationLoading(false);
			toast({
				title: `Error: ${errorMessage}`,
				type: 'error',
			});
		},
	});

	const assignmentApplication = applicationTabs['ongoingApplications'].applications.find(
		application => application._id === viewAssignmentSearchParam,
	) as UIExtendedApplication;
	const onlineTestApplication = applicationTabs['ongoingApplications'].applications.find(
		application => application._id === viewOnlineTestSearchParam,
	) as UIExtendedApplication;
	const viewOfferApplication =
		applicationTabs['ongoingApplications'].applications.find(application => application._id === viewOfferSearchParam) ||
		(applicationTabs['closedApplications'].applications.find(
			application => application._id === viewOfferSearchParam,
		) as UIExtendedApplication);
	const viewUpdatedTermsApplication = applicationTabs['ongoingApplications'].applications.find(
		application => application._id === viewUpdatedTermsSearchParam,
	) as UIExtendedApplication;
	const getPositionTitle = (application: UIExtendedApplication) => {
		if (application.isDriveApplication) {
			return formatCareerPathType(application.position?.title as UserCareerPathType);
		}
		return formatCareerPathType(revertPositionTitle(application.position?.title as PositionTitle) as UserCareerPathType);
	};
	const offerApplications = applicationTabs['ongoingApplications'].applications.filter(application => {
		const hasOfferRolledOut = application.history?.some(history => history.newStatus === ApplicationStatus.OFFER_ROLLED_OUT);
		const isOfferAvailable =
			application.status === ApplicationStatus.OFFER_ROLLED_OUT ||
			((application.status === ApplicationStatus.PENDING_FOR_OPT_OUT_APPROVAL ||
				application.status === ApplicationStatus.CHANGED_OFFER_TERMS) &&
				hasOfferRolledOut);
		return Boolean(isOfferAvailable);
	});
	const driveOfferApplications = allApplications?.applications?.driveOfferApplications ?? [];
	const getRegisteredDriveId = (application: UIExtendedApplication): string | null => {
		const rd = application.position?.registeredDrive as unknown;
		if (rd == null) return null;
		if (typeof rd === 'object' && '_id' in (rd as object)) {
			return String((rd as { _id: string })._id);
		}
		return String(rd);
	};
	const showOfferSticky =
		(offerApplications.length > 0 || driveOfferApplications.length > 0) &&
		!(viewAssignmentSearchParam || viewOnlineTestSearchParam || viewOfferSearchParam || viewUpdatedTermsSearchParam);
	const stickyOfferCardCount = offerApplications.length + driveOfferApplications.length;

	useEffect(() => {
		if (
			assignmentApplication?.ongoingRound?.type &&
			![ApplicationRoundType.Assignment, ApplicationRoundType.CaseStudy].includes(
				assignmentApplication?.ongoingRound?.type,
			)
		) {
			// remove viewAssignmentSearchParam from url
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.delete('viewAssignment');
			setSearchParams(newSearchParams);
			navigate(`/my-applications?${newSearchParams.toString()}`);
		}
	}, [assignmentApplication?.ongoingRound?.type, navigate, searchParams, setSearchParams]);

	useEffect(() => {
		if (
			onlineTestApplication?.ongoingRound?.type &&
			onlineTestApplication?.ongoingRound?.type !== ApplicationRoundType.OnlineTest
		) {
			// remove viewOnlineTestSearchParam from url
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.delete('viewOnlineTest');
			setSearchParams(newSearchParams);
			navigate(`/my-applications?${newSearchParams.toString()}`);
		}
	}, [onlineTestApplication?.ongoingRound?.type, navigate, searchParams, setSearchParams]);

	useEffect(() => {
		if (
			viewOfferApplication?.status &&
			![
				ApplicationStatus.PENDING_FOR_OPT_OUT_APPROVAL,
				ApplicationStatus.OFFER_ROLLED_OUT,
				ApplicationStatus.CHANGED_OFFER_TERMS,
				ApplicationStatus.VERBALLY_PLACED,
				ApplicationStatus.PLACED,
			].includes(viewOfferApplication?.status)
		) {
			// remove viewOffer from url
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.delete('viewOffer');
			setSearchParams(newSearchParams);
			navigate(`/my-applications?${newSearchParams.toString()}`);
		}
	}, [viewOfferApplication?.status, searchParams, setSearchParams, navigate]);

	return (
		<div
			className={`pb-[200px] md:pb-[180px] ${showOfferSticky
					? stickyOfferCardCount > 1
						? 'pb-[340px] md:pb-[280px]'
						: 'pb-[280px] md:pb-[220px]'
					: ''
				}`}
		>
			<Seo title={seoInfo.title} description={seoInfo.description} />
			<Container className="w-[92%] max-w-[1280px] pt-2  md:pb-2 pb-10 sm:w-[96%]font-inter">
				<div className="md:mt-6 flex font-inter md:items-end justify-between border-b border-solid dark:border-[#555770] pb-[10px] items-center">
					<div>
						<div className="sm:hidden">
							<MyApplicationsBreadcrumbs />
						</div>
						<div className="text-lg font-inter font-medium dark:text-white flex items-center md:mt-2">
							<span
								onClick={() => {
									const newSearchParams = new URLSearchParams(searchParams);
									if (viewAssignmentSearchParam) {
										newSearchParams.delete('viewAssignment');
										setSearchParams(newSearchParams);
										navigate(`/my-applications?${newSearchParams.toString()}`);
									} else if (viewOnlineTestSearchParam) {
										newSearchParams.delete('viewOnlineTest');
										setSearchParams(newSearchParams);
										navigate(`/my-applications?${newSearchParams.toString()}`);
									} else if (viewOfferSearchParam) {
										newSearchParams.delete('viewOffer');
										setSearchParams(newSearchParams);
										navigate(`/my-applications?${newSearchParams.toString()}`);
									} else if (viewUpdatedTermsSearchParam) {
										newSearchParams.delete('viewUpdatedTerms');
										setSearchParams(newSearchParams);
										navigate(`/my-applications?${newSearchParams.toString()}`);
									} else {
										// navigate('/dashboard');
										navigate('/my-applications');
									}
								}}
								className="mr-3 inline-block align-bottom md:hidden"
							>
								<Img loading="lazy" src="/assets/arrowLeft.svg" alt="arrowLeft" width="24" height="24" />
							</span>
							Jobs
						</div>
					</div>
					{!(viewAssignmentSearchParam || viewOnlineTestSearchParam || viewOfferSearchParam || viewUpdatedTermsSearchParam) ? (
						<div className="flex justify-center">
							<Pagination
								showIcons
								color="purplePlaced"
								className="-mt-2"
								layout={isMobile ? 'navigation' : 'pagination'}
								currentPage={Number(page)}
								totalPages={Math.ceil(applicationTabs[selectedTab]?.total / Number(limit))}
								onPageChange={page => {
									searchParams.set('page', String(page || 1));
									setSearchParams(searchParams);
								}}
							/>
						</div>
					) : null}
				</div>

				{viewAssignmentSearchParam || viewOnlineTestSearchParam || viewOfferSearchParam || viewUpdatedTermsSearchParam ? <DetailStyleLoader /> : null}
				{viewAssignmentSearchParam ? (
					<div className="dp-detail dp-form dp-fade pt-6">
						<ViewAssignment application={assignmentApplication} />
					</div>
				) : viewOnlineTestSearchParam ? (
					<div className="dp-detail dp-form dp-fade pt-6">
						<ViewOnlineTest application={onlineTestApplication} />
					</div>
				) : viewOfferSearchParam ? (
					<div className="dp-detail dp-form dp-fade h-[400px]">
						<ViewOffer application={viewOfferApplication} />
					</div>
				) : viewUpdatedTermsSearchParam ? (
					<div className="dp-detail dp-form dp-fade h-[400px]">
						<ViewUpdatedTerms application={viewUpdatedTermsApplication} />
					</div>
				) : (
					<div className="md:grid md:grid-cols-[minmax(0,1fr)_460px] md:gap-8 md:items-start">
						{/* Drives sit above the list on small screens and in the free right-hand column on larger ones */}
						<div className="md:hidden col-span-full">
							<UpcomingDrives variant="row" />
						</div>
					<div className="min-w-0">
						{/* Recommended Filter Toggle */}
						<div className="mt-[16px] mb-[16px] flex items-center">
							<ToggleSwitch
								theme={{
									toggle: {
										checked: {
											color: {
												blue: 'bg-[#7777E7] border-[#7777E7]',
											},
										},
									},
								}}
								checked={showRecommendedOnly}
								label="Show Recommended Only"
								onChange={() => {
									setShowRecommendedOnly(prev => !prev);
									const newSearchParams = new URLSearchParams(searchParams);
									newSearchParams.set('page', '1');
									setSearchParams(newSearchParams);
								}}
							/>
						</div>
						{/* Tabs */}
						<div
							id="applications-container"
							className="mt-[16px] flex gap-4 w-full overflow-auto whitespace-nowrap hide-scrollbar border-b-[1px] dark:border-[#555770] scroll-smooth"
						>
							{Object.keys(applicationTabs).map((tab: string) => {
								return (
									<div
										key={applicationTabs[tab].text}
										onClick={() => {
											if (tab === 'ongoingApplications') {
												const parentContainer = document.getElementById('applications-container');
												if (parentContainer) {
													parentContainer.scrollLeft = parentContainer.scrollWidth;
												}
											} else if (tab === 'appliedApplications') {
												const parentContainer = document.getElementById('applications-container');
												if (parentContainer) {
													parentContainer.scrollLeft = 0;
												}
											}
											sendTrackingEvent({
												event: 'my_applications_tab',
												tab_name: applicationTabs[tab].text,
												eventLabel: location.pathname,
											});
											setSelectedTab(tab as UIApplicationType);
											const newSearchParams = new URLSearchParams(searchParams);
											newSearchParams.set('page', '1'); // Set page to 1 for reseting pagination
											newSearchParams.set('applicationType', tab); // Set new or update existing param
											setSearchParams(newSearchParams);
										}}
										className={`px-[24px] py-[8px] cursor-pointer ${selectedTab === tab ? 'border-b-[1.5px] border-[#7777E7]' : ''
											} `}
									>
										<p
											className={`font-inter text-[14px] font-[500] leading-[20px] ${selectedTab === tab ? 'text-[#7777E7]' : 'dark:text-[#E4E4EB] text-[#555770]'
												}`}
										>{`${applicationTabs[tab].text} (${applicationTabs[tab].total})`}</p>
									</div>
								);
							})}
						</div>

						{isAllApplicationsLoading ? (
							<div className="mt-10">
								{/* <Loader /> */}
								<NewCreatingPositionLoader
									showAnimation={true}
									progressBar={false}
									customClassCss="!w-[90%] md:!w-[30%] mx-auto "
								/>
							</div>
						) : (
							<div className="mt-[20px] ">
								{applicationTabs[selectedTab].applications.length > 0 ? (
									<div className="grid gap-[16px] ">
										{applicationTabs[selectedTab].applications.map((application: UIExtendedApplication) => {
											return (
												<ApplicationCardV2
													key={application?._id}
													setIsNotInterestedModalOpen={setIsNotInterestedModalOpen}
													setIsCancelInterviewModalOpen={setIsCancelInterviewModalOpen}
													setIsLinkedInModalOpen={setIsLinkedInModalOpen}
													setIsLinkedInResumeModalOpen={setIsLinkedInResumeModalOpen}
													onCardClick={handleCardClick}
													application={application}
													setCurrentlySelectedApplication={setCurrentlySelectedApplication}
													optOutOfApplicationProcessLoading={optOutOfApplicationProcessLoading}
													createApplicationLoading={createApplicationLoading}
													createApplicationMutation={createApplicationMutation}
													selectedTab={selectedTab}
												/>
											);
										})}
									</div>
								) : (
									<div>
										<NoApplicationsContainer />
									</div>
								)}
							</div>
						)}
					</div>
					<UpcomingDrives variant="sidebar" />
					</div>
				)}

				{/* {showApplicationsList ? (
					<div className="mt-6 flex gap-9 sm:flex-col">
						<div className="flex grow flex-col gap-4">
							{applicationsData?.data?.applications &&
								applicationsData?.data?.applications.map(a => {
									return (
										<div key={a._id}>
											<ApplicationCard
												id={a._id}
												expiry={a.expiryDate}
												isLoadingApplying={false}
												onCardClick={handleCardClick}
												position={a.position as Position}
												status={a.status as ApplicationStatus}
											/>
										</div>
									);
								})}
						</div>
						<div className="w-[380px] sm:w-full">
							<RightSideDetailCard />
						</div>
					</div>
				) : (
					<div>
						<NoApplicationsContainer />
					</div>
				)} */}
			</Container>
			{showOfferSticky ? (
				<div className="fixed bottom-[72px] md:bottom-[16px] left-0 right-0 z-40 px-4">
					<div className="mx-auto flex w-full max-w-[900px] flex-col gap-3">
						{offerApplications.map(application => {
							const companyName = application.position?.company?.name || 'Company';
							const companyLogo = application.position?.company?.logo || '';
							return (
								<div
									key={application._id}
									className="dp-rise pointer-events-auto flex items-center justify-between gap-3 rounded-[16px] border border-[#E8E8F0] bg-white/95 px-4 py-3 shadow-[0_8px_24px_rgba(20,20,60,0.12)] backdrop-blur dark:border-[#3F3F3F] dark:bg-[#111E2C]"
								>
									<div className="flex min-w-0 items-center gap-3">
										<CompanyLogo name={companyName} logo={companyLogo} url={application.position?.company?.url} size={44} />
										<div className="min-w-0">
											<p className="truncate text-[15px] font-[700] leading-[20px] text-[#111E2C] dark:text-white">
												{getPositionTitle(application)}
											</p>
											<p className="truncate text-[13px] font-[500] leading-[18px] text-[#555770] dark:text-[#C7C9D9]">
												{companyName}
											</p>
										</div>
									</div>
									<Button
										color="purplePlaced"
										size={'md'}
										className="!rounded-[12px] !border-0 !bg-gradient-to-b !from-[#5B4FF0] !to-[#3027D8] !text-white !shadow-[0_4px_12px_rgba(79,70,229,0.25)] hover:!opacity-95 [&>span]:!px-5 [&>span]:!py-2 [&>span]:!text-[14px] [&>span]:!font-[700] [&>span]:!whitespace-nowrap"
										onClick={() => {
											const newSearchParams = new URLSearchParams(searchParams);
											newSearchParams.set('viewOffer', application._id || '');
											setSearchParams(newSearchParams);
										}}
									>
										View Offer
									</Button>
								</div>
							);
						})}
						{driveOfferApplications.map(application => {
							const driveId = getRegisteredDriveId(application);
							if (!driveId || !application._id) return null;
							const companyName = application.position?.company?.name || 'Company';
							const companyLogo = application.position?.company?.logo || '';
							return (
								<div
									key={`drive-offer-${application._id}`}
									className="dp-rise pointer-events-auto flex items-center justify-between gap-3 rounded-[16px] border border-[#E8E8F0] bg-white/95 px-4 py-3 shadow-[0_8px_24px_rgba(20,20,60,0.12)] backdrop-blur dark:border-[#3F3F3F] dark:bg-[#111E2C]"
								>
									<div className="flex min-w-0 items-center gap-3">
										<CompanyLogo name={companyName} logo={companyLogo} url={application.position?.company?.url} size={44} />
										<div className="min-w-0">
											<p className="truncate text-[15px] font-[700] leading-[20px] text-[#111E2C] dark:text-white">
												{getPositionTitle(application)}
											</p>
											<p className="truncate text-[13px] font-[500] leading-[18px] text-[#555770] dark:text-[#C7C9D9]">
												{companyName}
											</p>
										</div>
									</div>
									<Button
										color="purplePlaced"
										size={'md'}
										className="!rounded-[12px] !border-0 !bg-gradient-to-b !from-[#5B4FF0] !to-[#3027D8] !text-white !shadow-[0_4px_12px_rgba(79,70,229,0.25)] hover:!opacity-95 [&>span]:!px-5 [&>span]:!py-2 [&>span]:!text-[14px] [&>span]:!font-[700] [&>span]:!whitespace-nowrap"
										onClick={() => {
											navigate(
												`/offline-drive-day-opportunities?driveId=${encodeURIComponent(driveId)}&viewOffer=${encodeURIComponent(
													application._id || '',
												)}`,
											);
										}}
									>
										View Offer
									</Button>
								</div>
							);
						})}
					</div>
				</div>
			) : null}
			<ApplicationDrawer
				isApplicationDrawerOpen={isApplicationDrawerOpen}
				setIsApplicationDrawerOpen={setIsApplicationDrawerOpen}
				detail={applicationDrawerDetail}
				showCta={showCta}
				myApplicationStatus={myApplicationStatus}
				onApplyModalOpen={() => setIsApplyModalOpen(true)}
				setIsLinkedInResumeModalOpen={setIsLinkedInResumeModalOpen}
				setCurrentlySelectedApplication={setCurrentlySelectedApplication}
				createApplicationMutation={createApplicationMutation}
				application={currentlySelectedApplication}
			/>

			<NotInterestedModal
				isOpen={isNotInterestedModalOpen}
				onClose={() => {
					setIsNotInterestedModalOpen(false);
				}}
				application={currentlySelectedApplication}
				optOutOfApplicationProcessMutation={optOutOfApplicationProcessMutation}
			/>
			<CancelInterviewModal
				isOpen={isCancelInterviewModalOpen}
				onClose={() => {
					setIsCancelInterviewModalOpen(false);
				}}
			/>
			<LinkedInResumeModal
				application={currentlySelectedApplication}
				showLinkedInButton={!currentlySelectedApplication.user?.profile.linkedInProfile}
				isOpen={isLinkedInResumeModalOpen}
				onClose={() => {
					setIsLinkedInResumeModalOpen(false);
				}}
				createApplicationLoading={createApplicationLoading}
				createApplicationMutation={createApplicationMutation}
			/>
		</div>
	);
};

export default MyApplications;
